import * as React from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import asyncAction from 'modules/core/lib/asyncAction'

import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import supplementObservations from 'modules/payroll_bundle/store/collections/supplementObservations'
import supplementValidations from 'modules/payroll_bundle/store/collections/supplementValidations'

interface DataContextType {
  loading: boolean
}

interface Params {
  date?: moment.Moment
  frequency?: string
  loadingSupplementData: boolean
  policyPeriodId?: string | null | undefined
  supplementsStore: SupplementsFormStore
}

const SupplementExtraDataContext = React.createContext<DataContextType | null | undefined>(null)

export const SupplementExtraDataProvider = ({
  children,
  date,
  frequency,
  policyPeriodId,
  loadingSupplementData,
  supplementsStore,
}: React.PropsWithChildren<Params>) => {
  const [loading, setLoading] = React.useState<boolean>(true)
  const { access } = useCurrentSession()
  React.useEffect(() => {
    const fetch = async () => {
      const action = async () => {
        const supplementObservationsData = await supplementObservations.fetch()
        supplementsStore.buildSupplementObservationForms(supplementObservationsData)

        await supplementValidations.fetch()

        const currentUserValidations = supplementValidations.filter({
          author_id: access.id,
        })
        supplementsStore.buildSupplementValidationForms(currentUserValidations)
      }

      await asyncAction(action, {
        successFlash: false,
        errorFlash: true,
      })
      setLoading(false)
    }

    if (!loadingSupplementData) fetch()
  }, [date, frequency, policyPeriodId, loadingSupplementData]) // eslint-disable-line react-hooks/exhaustive-deps

  const value = { loading }

  return (
    <SupplementExtraDataContext.Provider value={value}>
      {children}
    </SupplementExtraDataContext.Provider>
  )
}

export const useSupplementExtraData = (): DataContextType => {
  const supplementExtraData = React.useContext(SupplementExtraDataContext)

  if (!supplementExtraData) {
    throw new Error('useSupplementExtraData must be used within a SupplementExtraDataProvider')
  }

  return supplementExtraData
}
